/**
 * DraftCards
 *
 * Displays draft checklists as cards above the checklists table.
 * Reads the shared admin draft list and renders clickable cards with name,
 * truncated description, and relative timestamp. Hidden when empty.
 */

import {
    adminDraftQueryKeys,
    deleteDraft,
    useDrafts,
} from "@/api/clause-checklists/drafts";
import { Modal } from "@/components/shared/Modal";
import {
    alpha,
    Box,
    Card,
    CardActionArea,
    CardContent,
    IconButton,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNowStrict } from "date-fns";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { DraftPublicationGroupLabel } from "./DraftPublicationGroupLabel";

export const DraftCards = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { data: drafts = [] } = useDrafts();
    const [deleteTarget, setDeleteTarget] = useState<number | null>(null);

    const discardMutation = useMutation({
        mutationFn: deleteDraft,
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: adminDraftQueryKeys.lists,
            });
            setDeleteTarget(null);
        },
    });

    const handleDelete = () => {
        if (deleteTarget !== null) {
            discardMutation.mutate(deleteTarget);
        }
    };

    // Hidden while loading, when the list can't be read, or when empty
    if (drafts.length === 0) return null;

    return (
        <>
            <Box sx={{ mb: 1.5 }}>
                {/* Section header */}
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.75}
                    sx={{ mb: 1 }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 600,
                            color: "text.secondary",
                        }}
                    >
                        Drafts
                    </Typography>
                    <Box
                        sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.06),
                            color: "text.disabled",
                            borderRadius: "8px",
                            px: 0.6,
                            py: "1px",
                            fontSize: "0.65rem",
                            fontWeight: 600,
                            lineHeight: 1.3,
                        }}
                    >
                        {drafts.length}
                    </Box>
                </Stack>

                {/* Cards */}
                <Stack
                    spacing={2}
                    sx={{
                        pb: 0.5,
                        width: "100%",
                    }}
                >
                    {drafts.map((draft) => (
                        <Card
                            key={draft.id}
                            variant="outlined"
                            sx={{
                                flexShrink: 0,
                                borderRadius: 2,
                                borderColor: alpha(
                                    theme.palette.primary.main,
                                    0.15
                                ),
                                transition:
                                    "border-color 0.15s ease, box-shadow 0.15s ease",
                                "&:hover": {
                                    borderColor: theme.palette.primary.main,
                                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
                                },
                            }}
                        >
                            <Box sx={{ position: "relative" }}>
                                <CardActionArea
                                    onClick={() =>
                                        navigate("/admin/checklists/generate", {
                                            state: { draftId: draft.id },
                                        })
                                    }
                                    sx={{ height: "100%" }}
                                >
                                    <CardContent sx={{ py: 2, px: 2.5 }}>
                                        <Stack spacing={0.75}>
                                            <Typography
                                                variant="subtitle2"
                                                fontWeight={600}
                                                noWrap
                                                sx={{ pr: 4 }}
                                            >
                                                {draft.name}
                                            </Typography>

                                            <DraftPublicationGroupLabel
                                                draft={draft}
                                            />

                                            {draft.description && (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        fontSize: "0.8rem",
                                                        lineHeight: 1.5,
                                                        overflowWrap:
                                                            "break-word",
                                                        wordBreak: "break-all",
                                                        display: "-webkit-box",
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient:
                                                            "vertical",
                                                        overflow: "hidden",
                                                    }}
                                                >
                                                    {draft.description}
                                                </Typography>
                                            )}

                                            <Typography
                                                variant="caption"
                                                color="text.disabled"
                                                sx={{ fontSize: "0.7rem" }}
                                            >
                                                {formatDistanceToNowStrict(
                                                    draft.updated_at,
                                                    { addSuffix: true }
                                                )}
                                            </Typography>
                                        </Stack>
                                    </CardContent>
                                </CardActionArea>
                                <IconButton
                                    size="small"
                                    aria-label={`Discard ${draft.name}`}
                                    onClick={() => setDeleteTarget(draft.id)}
                                    sx={{
                                        position: "absolute",
                                        top: 8,
                                        right: 8,
                                        color: "text.secondary",
                                        opacity: 0.4,
                                        "&:hover": {
                                            opacity: 1,
                                            color: "error.main",
                                        },
                                    }}
                                >
                                    <Trash2 size={14} />
                                </IconButton>
                            </Box>
                        </Card>
                    ))}
                </Stack>
            </Box>

            <Modal
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                title="Discard Draft"
                maxWidth="xs"
                showCancel
                actions={[
                    {
                        label: "Discard",
                        onClick: handleDelete,
                        variant: "contained",
                        color: "error",
                        disabled: discardMutation.isPending,
                    },
                ]}
            >
                <Typography variant="body2" color="text.secondary">
                    Are you sure you want to discard this draft? This action
                    cannot be undone.
                </Typography>
            </Modal>
        </>
    );
};
